import React, { useState, useEffect } from "react";
import { FiCalendar, FiShoppingCart, FiFileText, FiPackage, FiSettings, FiTruck, FiCheckCircle, FiClock, FiUser, FiClipboard, FiAward, FiXCircle } from "react-icons/fi";
import moment from "moment";
import StepCodeTag from "../../../components/shared/StepCodeTag";
import { HISTORY_STEP_CODES } from "../../../constants/workflowStepCodes";

const HistoryTab = ({ 
    orderData, 
    confirmCompletedModalState,
    setConfirmCompletedModalState,
    handleConfirmCompleted,
    isConfirmingCompleted,
    superadmin,
    manager,
    outright,
    hq,
    staff,
    purchaser
}) => {
    const [commenceDate, setCommenceDate] = useState("");

    const currencySymbol = process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM";
    const formatCurrency = (value) =>
        new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value || 0);

    const orderDiscount = parseFloat(orderData?.discount || 0);
    const orderGrossTotal = parseFloat(orderData?.amount || 0);
    const orderNetTotal = orderGrossTotal - orderDiscount;

    const statusesWithoutCompleteOrder = [
        "pending",
        "completed",
        "uploadedDraft",
        "draftApproved",
        "purchaseApproved",
    ];
    const showCompleteOrderButton =
        (superadmin || staff || outright) &&
        orderData?.status &&
        !statusesWithoutCompleteOrder.includes(orderData.status);

    const purchaseOrderItems = (orderData?.materials || []).filter((item) => {
        const quantity = item.quantity ?? item.amount ?? 0;
        const price = item.price || 0;
        return quantity > 0 && price > 0;
    });

    const purchaseOrdersTotal = purchaseOrderItems.reduce((sum, item) => {
        const quantity = item.quantity ?? item.amount ?? 0;
        const price = item.price || 0;
        return sum + price * quantity;
    }, 0);

    const expenseLabelMap = {
        creditCard: "Credit Card",
        commission: "Commission",
        freeGive: "Free Give",
        crane: "Crane",
        voucher: "Voucher",
        drawing: "Drawing",
        sponsor: "Sponsor",
        snSstCn: "SN SST CN",
        swSstCn: "SW SST CN",
        sstCn: "SST CN",
        addon: "Addon",
        threedDrawing: "3D Drawing",
        accessoryCosting: "Accessory Costing",
    };

    const expenseEntries = orderData?.expenses
        ? Object.entries(orderData.expenses)
              .map(([key, value]) => ({
                  key,
                  label:
                      expenseLabelMap[key] ||
                      key
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase()),
                  value: parseFloat(value || 0),
              }))
              .filter((entry) => entry.value > 0)
        : [];

    const expensesTotal = expenseEntries.reduce((sum, entry) => sum + entry.value, 0);
    const totalExpenses = purchaseOrdersTotal + expensesTotal;
    const profit = orderNetTotal - totalExpenses;

    // Prefill commenceDate if it exists
    useEffect(() => {
        if (orderData?.commenceDate) {
            setCommenceDate(new Date(orderData.commenceDate).toISOString().split('T')[0]);
        } else {
            setCommenceDate(new Date().toISOString().split('T')[0]);
        }
    }, [orderData]);

    // Handle modal body overflow
    useEffect(() => {
        if (confirmCompletedModalState) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [confirmCompletedModalState]);

    const handleConfirmCompletedSubmit = () => {
        handleConfirmCompleted(commenceDate);
        setConfirmCompletedModalState(false);
    };
    // Create timeline events array in the correct sequence
    const createTimelineEvents = () => {
        const events = [];

        // Commence Date (1st in sequence)
        if (orderData?.commenceDate) {
            events.push({
                date: orderData.commenceDate,
                title: "Project Commenced",
                description: "Project officially started",
                icon: FiCalendar,
                status: "completed",
                color: "indigo",
                sequence: 1,
            });
        }

        // Upload Draft Date (2nd in sequence)
        if (orderData?.uploadedDraftDate) {
            events.push({
                date: orderData.uploadedDraftDate,
                title: "Draft Uploaded",
                description: "Initial draft design uploaded",
                icon: FiFileText,
                status: "completed",
                color: "blue",
                sequence: 2,
            });
        }

        // Draft Rejected Date (2.5 in sequence)
        if (orderData?.draftRejectedDate) {
            events.push({
                date: orderData.draftRejectedDate,
                title: "Draft Rejected",
                description: "Draft was rejected",
                icon: FiXCircle,
                status: "rejected",
                color: "red",
                sequence: 2.5,
            });
        }

        // Blueprint Approval Date (3rd in sequence)
        if (orderData?.blueprintApprovalDate) {
            events.push({
                date: orderData.blueprintApprovalDate,
                title: "Blueprint Approved",
                description: "Blueprint design approved by production manager",
                icon: FiCheckCircle,
                status: "completed",
                color: "green",
                sequence: 3,
            });
        }

        // Salesman Rejected Date (3.3 in sequence)
        if (orderData?.salesmanRejectedDate) {
            events.push({
                date: orderData.salesmanRejectedDate,
                title: "Salesman Rejected Draft",
                description: "Salesman rejected the draft",
                icon: FiXCircle,
                status: "rejected",
                color: "red",
                sequence: 3.3,
            });
        }

        // Salesman Approved Date (3.4 in sequence)
        if (orderData?.salesmanApprovedDate) {
            events.push({
                date: orderData.salesmanApprovedDate,
                title: "Salesman Approved",
                description: "Salesman approved the blueprint design",
                icon: FiCheckCircle,
                status: "completed",
                color: "blue",
                sequence: 3.4,
            });
        }

        // Customer Rejected Date (3.5 in sequence)
        if (orderData?.customerRejectedDate) {
            events.push({
                date: orderData.customerRejectedDate,
                title: "Customer Rejected Draft",
                description: "Customer rejected the draft",
                icon: FiXCircle,
                status: "rejected",
                color: "red",
                sequence: 3.5,
            });
        }

        // Customer Approved Date (3.6 in sequence)
        if (orderData?.customerApprovedDate) {
            events.push({
                date: orderData.customerApprovedDate,
                title: "Customer Approved",
                description: "Customer approved the blueprint design",
                icon: FiUser,
                status: "completed",
                color: "purple",
                sequence: 3.6,
            });
        }

        // Purchase Order Rejected Date (3.8 in sequence)
        if (orderData?.purchaseOrderRejectedDate) {
            events.push({
                date: orderData.purchaseOrderRejectedDate,
                title: "Purchase Order Rejected",
                description: "Purchase order was rejected",
                icon: FiXCircle,
                status: "rejected",
                color: "red",
                sequence: 3.8,
            });
        }

        // Purchasing Date (4th in sequence)
        if (orderData?.purchasingDate) {
            events.push({
                date: orderData.purchasingDate,
                title: "Materials Purchased",
                description: "Required materials acquired",
                icon: FiPackage,
                status: "completed",
                color: "orange",
                sequence: 4,
            });
        }

        // Production Date (5th in sequence)
        if (orderData?.productionDate) {
            events.push({
                date: orderData.productionDate,
                title: "Production Started",
                description: "Manufacturing process initiated",
                icon: FiSettings,
                status: "completed",
                color: "purple",
                sequence: 5,
            });
        }

        // Production Approval Date (6th in sequence)
        if (orderData?.productionApprovalDate) {
            events.push({
                date: orderData.productionApprovalDate,
                title: "Production Approved",
                description: "Production quality approved",
                icon: FiCheckCircle,
                status: "completed",
                color: "pink",
                sequence: 6,
            });
        }

        // Production Rejected Date (5.5 in sequence)
        if (orderData?.productionRejectedDate) {
            events.push({
                date: orderData.productionRejectedDate,
                title: "Production Rejected",
                description: "Production was rejected",
                icon: FiXCircle,
                status: "rejected",
                color: "red",
                sequence: 5.5,
            });
        }

        // Installing Date (7th in sequence)
        if (orderData?.installingDate) {
            events.push({
                date: orderData.installingDate,
                title: "Installation Started",
                description: "On-site installation began",
                icon: FiTruck,
                status: "completed",
                color: "red",
                sequence: 7,
            });
        }

        // Install Approved Date (8th in sequence)
        if (orderData?.installApprovedDate) {
            events.push({
                date: orderData.installApprovedDate,
                title: "Survey Completed",
                description: "Customer survey completed and approved",
                icon: FiCheckCircle,
                status: "completed",
                color: "teal",
                sequence: 8,
            });
        }

        // Project Completed Date (9th in sequence)
        if (orderData?.commenceDate) {
            events.push({
                date: orderData.commenceDate,
                title: "Project Completed",
                description: "Project successfully completed and finalized",
                icon: FiAward,
                status: "completed",
                color: "green",
                sequence: 9,
            });
        }

        // Last Updated (always last)
        if (orderData?.updatedAt) {
            events.push({
                date: orderData.updatedAt,
                title: "Last Updated",
                description: "Most recent order modification",
                icon: FiClock,
                status: "info",
                color: "gray",
                sequence: 99
            });
        }

        // Sort events by sequence order (not by date)
        return events.sort((a, b) => a.sequence - b.sequence);
    };

    const timelineEvents = createTimelineEvents();

    const getStatusColor = (color) => {
        const colors = {
            blue: "bg-blue-500",
            indigo: "bg-indigo-500",
            green: "bg-green-500",
            orange: "bg-orange-500",
            purple: "bg-purple-500",
            emerald: "bg-emerald-500",
            cyan: "bg-cyan-500",
            teal: "bg-teal-500",
            pink: "bg-pink-500",
            red: "bg-red-500",
            amber: "bg-yellow-500",
            gray: "bg-gray-500"
        };
        return colors[color] || "bg-gray-500";
    };

    const getStatusBgColor = (color) => {
        const colors = {
            blue: "bg-blue-50 border-blue-200",
            indigo: "bg-indigo-50 border-indigo-200",
            green: "bg-green-50 border-green-200",
            orange: "bg-orange-50 border-orange-200",
            purple: "bg-purple-50 border-purple-200",
            emerald: "bg-emerald-50 border-emerald-200",
            cyan: "bg-cyan-50 border-cyan-200",
            teal: "bg-teal-50 border-teal-200",
            pink: "bg-pink-50 border-pink-200",
            amber: "bg-amber-50 border-amber-200",
            red: "bg-red-50 border-red-200",
            gray: "bg-gray-50 border-gray-200"
        };
        return colors[color] || "bg-gray-50 border-gray-200";
    };

    return (
        <div className="p-4 md:p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            {/* Enhanced Header Section */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2 flex-wrap">
                            <StepCodeTag code={HISTORY_STEP_CODES.projectTimeline} />
                            Order History & Timeline
                        </h1>
                        <p className="text-gray-600 text-sm">
                            Complete chronological overview of order progress and milestones
                        </p>
                    </div>
                    
                    {/* Complete Order Button - hidden when pending, completed, or other early workflow statuses */}
                    {showCompleteOrderButton && (
                        <div>
                            <button
                                className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-4 focus:ring-green-300 transform hover:scale-105 transition-all duration-200"
                                onClick={() => setConfirmCompletedModalState(true)}
                                disabled={isConfirmingCompleted}
                            >
                                <StepCodeTag code={HISTORY_STEP_CODES.completeOrder} />
                                <FiCheckCircle className="h-5 w-5 group-hover:animate-pulse" />
                                <span className="font-semibold">
                                    {isConfirmingCompleted ? 'Completing...' : 'Complete Order'}
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Desktop: Separate containers */}
            <div className="hidden md:block space-y-8">
                {/* Enhanced Order Timeline */}
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                    <div className="flex items-center mb-6 gap-2 flex-wrap">
                        <FiCalendar className="h-6 w-6 text-indigo-600" />
                        <h3 className="text-xl font-bold text-gray-900">Project Timeline</h3>
                    </div>
                    
                    {timelineEvents.length > 0 ? (
                        <>
                            {/* Desktop / Tablet: original vertical timeline (unchanged) */}
                            <div className="relative">
                                {/* Timeline line */}
                                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-indigo-200 via-purple-200 to-blue-200"></div>
                                <div className="space-y-6">
                                    {timelineEvents.map((event, index) => {
                                        const IconComponent = event.icon;
                                        return (
                                            <div key={index} className="relative flex items-start">
                                                {/* Timeline dot */}
                                                <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full ${getStatusColor(event.color)} shadow-lg`}>
                                                    <IconComponent className="h-5 w-5 text-white" />
                                                </div>
                                                {/* Timeline content */}
                                                <div className={`ml-6 flex-1 p-4 rounded-xl border-2 ${getStatusBgColor(event.color)} shadow-sm hover:shadow-md transition-shadow duration-200`}>
                                                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                                                        <h4 className="text-lg font-semibold text-gray-900">
                                                            {event.title}
                                                        </h4>
                                                        <span className="text-sm font-medium text-gray-500">
                                                            {moment(event.date).format('MMM DD, YYYY')}
                                                        </span>
                                                    </div>
                                                    <p className="text-gray-600 text-sm mb-2">
                                                        {event.description}
                                                    </p>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-500">
                                                            {moment(event.date).format('dddd, MMMM DD, YYYY [at] h:mm A')}
                                                        </span>
                                                        <span className="text-xs text-gray-400">
                                                            {moment(event.date).fromNow()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <FiCalendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                            <h4 className="text-lg font-semibold text-gray-500 mb-2">No Timeline Events</h4>
                            <p className="text-gray-400">Timeline events will appear as dates are added to the order.</p>
                        </div>
                    )}
                </div>

                {/* Enhanced Products Summary */}
                {(superadmin || purchaser || outright || manager) && (
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                    <div className="flex items-center mb-6 gap-2 flex-wrap">
                        <FiShoppingCart className="h-6 w-6 text-blue-600" />
                        <h3 className="text-xl font-bold text-gray-900">Order Financial Summary</h3>
                    </div>
                    {orderData?.products && orderData.products.length > 0 ? (
                        <div className="w-full">
                            <div className="space-y-6 max-w-full">
                                    
                                    {/* Products Section */}
                                    <div className="space-y-3">
                                        {orderData.products.map((product, index) => {
                                            const itemTotal = (product.price || 0) * (product.quantity || product.amount);
                                            const percentage = orderNetTotal > 0 ? (itemTotal / orderNetTotal * 100).toFixed(2) : 0;
                                            return (
                                                <div
                                                    key={index}
                                                    className="flex justify-between items-center bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200"
                                                >
                                                    <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0 mr-2">
                                                        <div className="w-7 h-7 md:w-8 md:h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                                            {index + 1}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-semibold text-gray-900 text-sm md:text-base truncate">
                                                                {product.code}
                                                            </div>
                                                            <div className="text-xs md:text-sm text-gray-600 break-words">
                                                                Qty: {product.quantity || product.amount} × {currencySymbol}{formatCurrency(product.price || 0)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="font-bold text-xs md:text-base text-gray-900 whitespace-nowrap flex-shrink-0">
                                                        {currencySymbol}{formatCurrency(itemTotal)} <span className="text-xs md:text-base text-gray-700 font-normal">({percentage}%)</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <div className="flex flex-col md:flex-row md:justify-between md:items-center bg-blue-50 p-3 md:p-4 rounded-lg border-2 border-blue-300 gap-2">
                                            <span className="font-bold text-sm md:text-base text-gray-900">
                                                Total Paid {orderData?.discount > 0 && <span className="text-xs font-normal text-gray-600">(after discount)</span>}
                                            </span>
                                            <span className="font-bold text-sm md:text-lg text-blue-600">
                                                {currencySymbol}{formatCurrency(orderNetTotal)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Purchase Orders Section */}
                                    {purchaseOrderItems.length > 0 && (
                                        <div className="space-y-3 pt-4 border-t-2 border-gray-200">
                                           
                                            {purchaseOrderItems.map((item, index) => {
                                                const itemTotal = (item.price || 0) * (item.quantity ?? item.amount ?? 0);
                                                const percentage = orderNetTotal > 0 ? (itemTotal / orderNetTotal * 100).toFixed(2) : 0;
                                                return (
                                                    <div
                                                        key={`po-${index}`}
                                                        className="flex justify-between items-center bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200"
                                                    >
                                                        <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0 mr-2">
                                                            <div className="w-7 h-7 md:w-8 md:h-8 bg-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                                                {index + 1}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="font-semibold text-gray-900 text-sm md:text-base truncate">
                                                                    {item.code || item.itemCode || "Material"}
                                                                </div>
                                                                <div className="text-xs md:text-sm text-gray-600 break-words">
                                                                    Qty: {item.quantity ?? item.amount ?? 0} × {currencySymbol}{formatCurrency(item.price || 0)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="font-bold text-xs md:text-base text-gray-900 whitespace-nowrap flex-shrink-0">
                                                            {currencySymbol}{formatCurrency(itemTotal)} <span className="text-xs md:text-base text-gray-700 font-normal">({percentage}%)</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div className="flex flex-col md:flex-row md:justify-between md:items-center bg-orange-50 p-3 md:p-4 rounded-lg border-2 border-orange-300 gap-2">
                                                <span className="font-bold text-sm md:text-base text-gray-900">
                                                    Total Purchase Orders
                                                </span>
                                                <span className="font-bold text-sm md:text-lg text-orange-600">
                                                    {currencySymbol}{formatCurrency(purchaseOrdersTotal)} <span className="text-sm md:text-lg text-orange-700 font-normal">({orderNetTotal > 0 ? (purchaseOrdersTotal / orderNetTotal * 100).toFixed(2) : 0}%)</span>
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Operational Expenses Section */}
                                    {expenseEntries.length > 0 && (
                                        <div className="space-y-3 pt-4 border-t-2 border-gray-200">
                                           
                                            {expenseEntries.map((expense, index) => {
                                                const percentage = orderNetTotal > 0 ? (expense.value / orderNetTotal * 100).toFixed(2) : 0;
                                                return (
                                                    <div
                                                        key={expense.key}
                                                        className="flex justify-between items-center bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200"
                                                    >
                                                        <div className="flex justify-start items-center space-x-2 md:space-x-3 flex-1 min-w-0 mr-2">
                                                            <div className="w-7 h-7 md:w-8 md:h-8 bg-red-600 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                                                {index + 1}
                                                            </div>
                                                            <div className="font-semibold text-gray-900 text-sm md:text-base truncate">
                                                                {expense.label}
                                                            </div>
                                                        </div>
                                                        <div className="font-bold text-xs md:text-base text-gray-900 whitespace-nowrap flex-shrink-0">
                                                            {currencySymbol}{formatCurrency(expense.value)} <span className="text-xs md:text-base text-gray-700 font-normal">({percentage}%)</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div className="flex flex-col md:flex-row md:justify-between md:items-center bg-red-50 p-3 md:p-4 rounded-lg border-2 border-red-300 gap-2">
                                                <span className="font-bold text-sm md:text-base text-gray-900">
                                                    Total Expenses
                                                </span>
                                                <span className="font-bold text-sm md:text-lg text-red-600">
                                                    {currencySymbol}{formatCurrency(expensesTotal)} <span className="text-sm md:text-lg text-red-700 font-normal">({orderNetTotal > 0 ? (expensesTotal / orderNetTotal * 100).toFixed(2) : 0}%)</span>
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Financial Summary */}
                                    <div className="space-y-3 pt-6 border-t-4 border-gray-300">
                                        <div className="flex flex-col md:flex-row md:justify-between md:items-center bg-gray-100 p-3 md:p-4 rounded-lg border-2 border-gray-300 gap-2">
                                            <span className="font-bold text-sm md:text-base text-gray-900">
                                                Total Cost
                                            </span>
                                            <span className="font-bold text-sm md:text-lg text-gray-900">
                                                {currencySymbol}{formatCurrency(totalExpenses)} <span className="text-sm md:text-lg text-gray-700 font-normal">({orderNetTotal > 0 ? (totalExpenses / orderNetTotal * 100).toFixed(2) : 0}%)</span>
                                            </span>
                                        </div>
                                        <div className={`flex flex-col md:flex-row md:justify-between md:items-center p-4 md:p-5 rounded-lg border-2 gap-2 ${profit >= 0 ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300' : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300'}`}>
                                            <span className="font-bold text-sm md:text-lg text-gray-900">
                                                Net Profit
                                            </span>
                                            <span className={`font-bold text-base md:text-2xl ${profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                {currencySymbol}{formatCurrency(profit)} <span className={`text-base md:text-2xl font-normal ${profit >= 0 ? "text-green-700" : "text-red-700"}`}>({orderNetTotal > 0 ? (profit / orderNetTotal * 100).toFixed(2) : 0}%)</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <FiPackage className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                            <h4 className="text-lg font-semibold text-gray-500 mb-2">No Products Found</h4>
                            <p className="text-gray-400">Products will appear here once they are added to the order.</p>
                        </div>
                    )}
                </div>
                )}
            </div>

            {/* Mobile: Unified container for both Timeline and Financial Summary */}
            <div className="md:hidden bg-white rounded-2xl shadow-xl p-4 border border-gray-200 -mx-4">
                {/* Project Timeline Section */}
                <div className="mb-8">
                    <div className="flex items-center mb-6 gap-2 flex-wrap">
                        <FiCalendar className="h-5 w-5 text-indigo-600" />
                        <h3 className="text-lg font-bold text-gray-900">Project Timeline</h3>
                    </div>
                    
                    {timelineEvents.length > 0 ? (
                        <div className="relative">
                            {/* Timeline line */}
                            <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-indigo-200 via-purple-200 to-blue-200"></div>
                            <div className="space-y-5">
                                {timelineEvents.map((event, index) => {
                                    const IconComponent = event.icon;
                                    return (
                                        <div key={`mobile-${index}`} className="relative flex items-start">
                                            {/* Timeline dot */}
                                            <div className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full ${getStatusColor(event.color)} shadow-lg flex-shrink-0`}>
                                                <IconComponent className="h-5 w-5 text-white" />
                                            </div>
                                            {/* Timeline content */}
                                            <div className={`ml-4 flex-1 p-4 rounded-xl border-2 ${getStatusBgColor(event.color)} shadow-sm`}>
                                                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                                                    <h4 className="text-base font-semibold text-gray-900">
                                                        {event.title}
                                                    </h4>
                                                    <span className="text-xs font-medium text-gray-500 flex-shrink-0">
                                                        {moment(event.date).format('MMM DD, YYYY')}
                                                    </span>
                                                </div>
                                                <p className="text-gray-600 text-sm mb-2">
                                                    {event.description}
                                                </p>
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-xs text-gray-500">
                                                        {moment(event.date).format('dddd, MMMM DD, YYYY [at] h:mm A')}
                                                    </span>
                                                    <span className="text-xs text-gray-400">
                                                        {moment(event.date).fromNow()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <FiCalendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                            <h4 className="text-lg font-semibold text-gray-500 mb-2">No Timeline Events</h4>
                            <p className="text-gray-400">Timeline events will appear as dates are added to the order.</p>
                        </div>
                    )}
                </div>

                {/* Order Financial Summary Section */}
                {(superadmin || purchaser || outright || manager) && (
                    <div className="pt-8 border-t-2 border-gray-200">
                        <div className="flex items-center mb-4 gap-2 flex-wrap">
                            <FiShoppingCart className="h-5 w-5 text-blue-600" />
                            <h3 className="text-lg font-bold text-gray-900">Order Financial Summary</h3>
                        </div>
                        {orderData?.products && orderData.products.length > 0 ? (
                            <div className="w-full">
                                <div className="space-y-6 max-w-full">
                                        
                                        {/* Products Section */}
                                        <div className="space-y-3">
                                            {orderData.products.map((product, index) => {
                                                const itemTotal = (product.price || 0) * (product.quantity || product.amount);
                                                const percentage = orderNetTotal > 0 ? (itemTotal / orderNetTotal * 100).toFixed(2) : 0;
                                                return (
                                                    <div
                                                        key={index}
                                                        className="flex justify-between items-center bg-gray-50 p-3 rounded-lg border border-gray-200"
                                                    >
                                                        <div className="flex items-center space-x-2 flex-1 min-w-0 mr-2">
                                                            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                                                {index + 1}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <div className="font-semibold text-gray-900 text-sm truncate">
                                                                    {product.code}
                                                                </div>
                                                                <div className="text-xs text-gray-600 break-words">
                                                                    Qty: {product.quantity || product.amount} × {currencySymbol}{formatCurrency(product.price || 0)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="font-bold text-xs text-gray-900 whitespace-nowrap flex-shrink-0">
                                                            {currencySymbol}{formatCurrency(itemTotal)} <span className="text-xs text-gray-700 font-normal">({percentage}%)</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div className="flex flex-col gap-2 bg-blue-50 p-3 rounded-lg border-2 border-blue-300">
                                                <span className="font-bold text-sm text-gray-900">
                                                    Total Paid {orderData?.discount > 0 && <span className="text-xs font-normal text-gray-600">(after discount)</span>}
                                                </span>
                                                <span className="font-bold text-sm text-blue-600">
                                                    {currencySymbol}{formatCurrency(orderNetTotal)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Purchase Orders Section */}
                                        {purchaseOrderItems.length > 0 && (
                                            <div className="space-y-3 pt-4 border-t-2 border-gray-200">
                                               
                                                {purchaseOrderItems.map((item, index) => {
                                                    const itemTotal = (item.price || 0) * (item.quantity ?? item.amount ?? 0);
                                                    const percentage = orderNetTotal > 0 ? (itemTotal / orderNetTotal * 100).toFixed(2) : 0;
                                                    return (
                                                        <div
                                                            key={`po-${index}`}
                                                            className="flex justify-between items-center bg-gray-50 p-3 rounded-lg border border-gray-200"
                                                        >
                                                            <div className="flex items-center space-x-2 flex-1 min-w-0 mr-2">
                                                                <div className="w-7 h-7 bg-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                                                    {index + 1}
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="font-semibold text-gray-900 text-sm truncate">
                                                                        {item.code || item.itemCode || "Material"}
                                                                    </div>
                                                                    <div className="text-xs text-gray-600 break-words">
                                                                        Qty: {item.quantity ?? item.amount ?? 0} × {currencySymbol}{formatCurrency(item.price || 0)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="font-bold text-xs text-gray-900 whitespace-nowrap flex-shrink-0">
                                                                {currencySymbol}{formatCurrency(itemTotal)} <span className="text-xs text-gray-700 font-normal">({percentage}%)</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <div className="flex flex-col gap-2 bg-orange-50 p-3 rounded-lg border-2 border-orange-300">
                                                    <span className="font-bold text-sm text-gray-900">
                                                        Total Purchase Orders
                                                    </span>
                                                    <span className="font-bold text-sm text-orange-600">
                                                        {currencySymbol}{formatCurrency(purchaseOrdersTotal)} <span className="text-sm text-orange-700 font-normal">({orderNetTotal > 0 ? (purchaseOrdersTotal / orderNetTotal * 100).toFixed(2) : 0}%)</span>
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Operational Expenses Section */}
                                        {expenseEntries.length > 0 && (
                                            <div className="space-y-3 pt-4 border-t-2 border-gray-200">
                                               
                                                {expenseEntries.map((expense, index) => {
                                                    const percentage = orderNetTotal > 0 ? (expense.value / orderNetTotal * 100).toFixed(2) : 0;
                                                    return (
                                                        <div
                                                            key={expense.key}
                                                            className="flex justify-between items-center bg-gray-50 p-3 rounded-lg border border-gray-200"
                                                        >
                                                            <div className="flex justify-start items-center space-x-2 flex-1 min-w-0 mr-2">
                                                                <div className="w-7 h-7 bg-red-600 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                                                    {index + 1}
                                                                </div>
                                                                <div className="font-semibold text-gray-900 text-sm truncate">
                                                                    {expense.label}
                                                                </div>
                                                            </div>
                                                            <div className="font-bold text-xs text-gray-900 whitespace-nowrap flex-shrink-0">
                                                                {currencySymbol}{formatCurrency(expense.value)} <span className="text-xs text-gray-700 font-normal">({percentage}%)</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <div className="flex flex-col gap-2 bg-red-50 p-3 rounded-lg border-2 border-red-300">
                                                    <span className="font-bold text-sm text-gray-900">
                                                        Total Expenses
                                                    </span>
                                                    <span className="font-bold text-sm text-red-600">
                                                        {currencySymbol}{formatCurrency(expensesTotal)} <span className="text-sm text-red-700 font-normal">({orderNetTotal > 0 ? (expensesTotal / orderNetTotal * 100).toFixed(2) : 0}%)</span>
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Financial Summary */}
                                        <div className="space-y-3 pt-6 border-t-4 border-gray-300">
                                            <div className="flex flex-col gap-2 bg-gray-100 p-3 rounded-lg border-2 border-gray-300">
                                                <span className="font-bold text-sm text-gray-900">
                                                    Total Cost
                                                </span>
                                                <span className="font-bold text-sm text-gray-900">
                                                    {currencySymbol}{formatCurrency(totalExpenses)} <span className="text-sm text-gray-700 font-normal">({orderNetTotal > 0 ? (totalExpenses / orderNetTotal * 100).toFixed(2) : 0}%)</span>
                                                </span>
                                            </div>
                                            <div className={`flex flex-col gap-2 p-4 rounded-lg border-2 ${profit >= 0 ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300' : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300'}`}>
                                                <span className="font-bold text-sm text-gray-900">
                                                    Net Profit
                                                </span>
                                                <span className={`font-bold text-base ${profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                    {currencySymbol}{formatCurrency(profit)} <span className={`text-base font-normal ${profit >= 0 ? "text-green-700" : "text-red-700"}`}>({orderNetTotal > 0 ? (profit / orderNetTotal * 100).toFixed(2) : 0}%)</span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <FiPackage className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                <h4 className="text-lg font-semibold text-gray-500 mb-2">No Products Found</h4>
                                <p className="text-gray-400">Products will appear here once they are added to the order.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Confirm Completed Modal */}
            {confirmCompletedModalState && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
                        <div className="flex items-center mb-6 gap-2 flex-wrap">
                            <FiCheckCircle className="h-8 w-8 text-green-600" />
                            <h3 className="text-2xl font-bold text-gray-900">
                                Complete Order
                            </h3>
                        </div>
                        
                        <div className="mb-6">
                            <p className="text-gray-600 mb-4">
                                This will mark the order as completed. Please confirm the commence date:
                            </p>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Commence Date:
                                </label>
                                <input
                                    type="date"
                                    value={commenceDate}
                                    onChange={(e) => setCommenceDate(e.target.value)}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                />
                            </div>
                        </div>
                        
                        <div className="flex space-x-4">
                            <button
                                onClick={() => setConfirmCompletedModalState(false)}
                                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
                                disabled={isConfirmingCompleted}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmCompletedSubmit}
                                disabled={isConfirmingCompleted || !commenceDate}
                                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            >
                                <StepCodeTag code={HISTORY_STEP_CODES.confirmCompleteOrder} />
                                {isConfirmingCompleted ? 'Completing...' : 'Complete Order'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HistoryTab;
